import { DatePipe } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnInit,
} from '@angular/core';
import {
  DprTableOfColumnsDetailReport,
  DprTableOfColumnsReport,
} from '@report/models/dpr-table-of-columns-report.model';
import { StaticMessage } from '@shared/enums';
import { DataType, SelectionItem } from '@shared/models';
import { DprTableOfColumns } from '@shared/models/dpr';
import {
  TableOfColumnItem,
  TableOfColumnItemValue,
} from '@shared/models/project-setup';
import {
  getValue,
  isDataTypeSummarizable,
  buildBooleanValues,
} from '@shared/utils';

import cloneDeep from 'lodash.clonedeep';

@Component({
  selector: 'app-report-table-of-columns',
  templateUrl: './report-table-of-columns.component.html',
  styleUrls: ['./report-table-of-columns.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportTableOfColumnsComponent implements OnInit {
  @Input({ required: true })
  tableOfColumnItemDataTypeValueList: TableOfColumnItemValue[];
  @Input({ required: true }) tableOfColumnItems: TableOfColumnItem[];
  @Input({ required: true }) dprTableOfColumns: DprTableOfColumns;
  @Input({ required: true }) isSummaryEnabled: boolean;
  @Input({ required: true }) summaryLabel: string;
  @Input({ required: true }) customName: string;
  filteredTableOfColumnItems: TableOfColumnItem[] = [];
  tableOfColumnsReport: DprTableOfColumnsReport = {
    tableOfColumnsDetailReport: [],
    columnWidth: 0,
    columnNumber: 0,
  };

  StaticMessage = StaticMessage;

  constructor(private datePipe: DatePipe) {}

  ngOnInit(): void {
    if (this.dprTableOfColumns) {
      this.filtertableOfColumnItems();

      let dprTableOfColumnsDetailItemsTotal = {};
      if (this.isSummaryEnabled) {
        this.summaryLabel = String.isNullOrWhiteSpace(this.summaryLabel)
          ? 'Summary'
          : this.summaryLabel;

        this.tableOfColumnItems.forEach(
          (item) =>
            (dprTableOfColumnsDetailItemsTotal = {
              ...dprTableOfColumnsDetailItemsTotal,
              [item.name]: isDataTypeSummarizable(item.dataTypeId) ? 0 : '-',
            })
        );
      }

      this.dprTableOfColumns.dprTableOfColumnsDetails.forEach(
        (dprTableOfColumnsDetail) => {
          const toAdd: DprTableOfColumnsDetailReport = {
            dprTableOfColumnsDetailItems: [],
          };

          this.tableOfColumnItems.forEach((tableOfColumnItem) => {
            const detail = (
              dprTableOfColumnsDetail.dprTableOfColumnDetailItems || []
            ).find((x) => x.tableOfColumnItemId === tableOfColumnItem.id);

            let listOptions: SelectionItem[];

            switch (tableOfColumnItem.dataTypeId) {
              case DataType.List:
                listOptions = cloneDeep(this.tableOfColumnItemDataTypeValueList)
                  .filter((w) => w.tableOfColumnItemId === tableOfColumnItem.id)
                  .map((v) => ({ id: v.id, name: v.value }));
                break;
              case DataType.Boolean:
                listOptions = buildBooleanValues(
                  tableOfColumnItem.booleanTerminologyId
                ).map((x) => ({ id: x.id, name: x.value }));
                break;
              default:
                listOptions = [];
            }

            toAdd.dprTableOfColumnsDetailItems.push({
              value: detail
                ? `${getValue(
                    detail.value,
                    tableOfColumnItem.dataTypeId,
                    listOptions,
                    this.datePipe
                  )}`
                : '-',
            });

            if (
              this.isSummaryEnabled &&
              isDataTypeSummarizable(tableOfColumnItem.dataTypeId)
            ) {
              dprTableOfColumnsDetailItemsTotal[tableOfColumnItem.name] +=
                detail?.value ? parseFloat(detail.value) : 0;
            }
          });

          this.tableOfColumnsReport.tableOfColumnsDetailReport.push(toAdd);
        }
      );

      if (
        this.isSummaryEnabled &&
        this.tableOfColumnsReport.tableOfColumnsDetailReport.length
      ) {
        const dprTableOfColumnsTotal: DprTableOfColumnsDetailReport = {
          dprTableOfColumnsDetailItems: [],
          isSummary: true,
        };

        this.tableOfColumnItems.forEach((item) => {
          dprTableOfColumnsDetailItemsTotal[item.name] = this.formatNumber(
            dprTableOfColumnsDetailItemsTotal[item.name],
            item.dataTypeId
          );

          dprTableOfColumnsTotal.dprTableOfColumnsDetailItems.push({
            value: item.includeInSummary
              ? dprTableOfColumnsDetailItemsTotal[item.name]
              : '-',
          });
        });

        this.tableOfColumnsReport.tableOfColumnsDetailReport.push(
          dprTableOfColumnsTotal
        );
      }

      // Plus 1 is needed in order for the table to account for the sequence column
      this.tableOfColumnsReport.columnWidth =
        100 / this.filteredTableOfColumnItems.length + 1;
      this.tableOfColumnsReport.columnNumber =
        this.filteredTableOfColumnItems.length + 1;
    }
  }

  private filtertableOfColumnItems(): void {
    this.filteredTableOfColumnItems = this.filteredTableOfColumnItems.concat(
      this.tableOfColumnItems.filter(
        (x) => x.tableOfColumnId === this.dprTableOfColumns.tableOfColumnId
      )
    );
  }

  private formatNumber(value: number, dataTypeId: number): string {
    switch (dataTypeId) {
      case DataType.Number: {
        const formattedValue =
          value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
        return formattedValue;
      }
      case DataType.Percentage: {
        const formattedValue =
          value % 1 === 0 ? value.toFixed(0) + '%' : value.toFixed(2) + '%';
        return formattedValue;
      }
      default: {
        return value.toString();
      }
    }
  }
}
